/**
 * Módulo del tutorial: Linaje — quiénes crían mejor (KAN-232).
 *
 * Análisis Descendencia: el ranking de madres y toros según la cría que
 * dejan (peso inicial, ganancia total, GDP promedio) + el árbol genealógico
 * de cada animal. Para elegir reposición y descarte con fundamento.
 *
 * Anclas: menu-analisis-descendencia (menú Ganado),
 * descendencia-ranking (switcher de métrica + tablas).
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';

export const linajeModule: TutorialModuleDef = {
  id: 'linaje',
  title: 'Quiénes crían mejor',
  description: 'El ranking de tus madres y toros según la cría que dejan — peso inicial, ganancia y GDP — más el árbol genealógico. Reposición con fundamento.',
  emoji: '🧬',
  image: 'images/tutorial/linaje.png',
  permission: { module: 'animals', action: 'view' },
  grupo: 'produccion',
  route: '/analisis-descendencia',
  steps: [
    {
      id: 'concepto-linaje',
      route: '/tutorial',
      text: 'No todas las vacas son iguales: algunas paren crías que engordan solas. El ranking de descendencia te las ordena — la genética de tu campo, en números.',
      narration:
        'Vamos a un terreno donde el ojo del criador hace magia, pero los números ayudan un montón: no todas las vacas crían igual. Hay madres que paren terneros que engordan solos, y otras que cuestan de criar. El Análisis de Descendencia ordena a tus madres y toros según la cría que dejan: la genética de tu campo, vuelta ranking.',
    },
    {
      id: 'ir-a-descendencia',
      target: '[data-ar-guide="menu-analisis-descendencia"]',
      label: 'Análisis Descendencia (menú Ganado)',
      text: 'En el menú, sección Ganado, tocá "Análisis Descendencia" (en celular: menú ☰).',
      narration:
        'Andá al menú, sección Ganado, y tocá Análisis Descendencia. Para aprovecharlo, cargá madre y padre en la ficha de cada cría — sin eso la app no puede armar las familias. Cargado el linaje, acá se ordena solo.',
    },
    {
      id: 'el-ranking',
      route: '/analisis-descendencia',
      target: '[data-ar-guide="descendencia-metric-pesoInicial"]',
      extraTargets: [
        '[data-ar-guide="descendencia-metric-gananciaKg"]',
        '[data-ar-guide="descendencia-metric-gdp"]',
      ],
      label: 'Ranking (elegí la métrica)',
      advanceOnClick: false,
      text: 'El ranking, con su switcher de métrica: peso inicial de la cría, ganancia total, o GDP promedio. Cambiás la métrica y las tablas se reordenan al toque.',
      narration:
        'Mirá las tablas: arriba las madres que crían mejor, más abajo las que cuestan. Y con el switcher de arriba elegís la vara: el peso inicial de las crías, cuánto engordaron en total, o la ganancia diaria promedio. Cambiás la métrica y el ranking se reordena solo. Cada una cuenta una historia distinta de la misma vaca.',
    },
    {
      id: 'toros-y-arbol',
      route: '/analisis-descendencia',
      text: 'También rankea TOROS: el que preña cincuenta vacas multiplica su genética — acá se ve cuál de verdad transmite. Y tocando la CARAVANA de cualquier fila se abre su árbol genealógico ("ficha →" te lleva a su ficha).',
      narration:
        'Y no te saltees la tabla de toros: un toro preña medio rodeo, así que su genética se multiplica por cincuenta. Acá ves cuál transmite de verdad y cuál sólo tiene buen pedigrí de papel. Y fijate que en cada fila la caravana es un atajo: la tocás y se abre el árbol genealógico de ese animal, con toda su familia desplegada. Si querés la ficha completa, es el enlace que está al lado.',
    },
    {
      id: 'abrir-el-arbol',
      target: '[data-ar-guide="descendencia-row-pedigree"]',
      label: 'Tocá la caravana (abre el árbol)',
      text: 'Probalo: tocá la caravana de la primera del ranking — se abre su ÁRBOL GENEALÓGICO.',
      narration:
        'Probalo con la mejor de la lista: tocá su caravana, ese número verde, y mirá lo que se abre.',
    },
    {
      id: 'ver-el-arbol',
      text: '¡Este es el árbol! Padres, abuelos y toda la cría de esa madre — su historia reproductiva desplegada. Volvés con ✓ y seguís.',
      narration:
        '¿Lo ves? Este es el árbol genealógico: hacia arriba, la madre y el padre de tu vaca, y los abuelos si los tenés cargados; hacia abajo, toda la cría que dejó. La historia reproductiva de una familia entera, desplegada como un organito. Cuando termines de mirar, le das al check y seguimos.',
    },
    {
      id: 'cierre-linaje',
      route: '/analisis-descendencia',
      text: 'El uso final: elegir reposición de las mejores y mandar al remate las que cuestan. El rodeo mejora solo, tacto a tacto, con tus propios datos.',
      narration:
        'Y así se usa en serio: cuando toque elegir reposición, las madres de arriba de la tabla te dan las hijas; las de abajo, el boleto al remate. Sin comprar genética de afuera, tu rodeo mejora solo, año a año, con tus propios datos. Criar bien es elegir bien, criollo.',
    },
  ],
  completionCheck: async () => false,
};
